import { useFrame } from '@react-three/fiber'
import { FrontSide, ShaderMaterial, Vector2 } from 'three'
import { tunnelShaderOffsetX, tunnelShaderOffsetY } from '../../config/constants'

export function useUpdateTunnelShader() {
  useFrame((state) => {
    state.gl.getDrawingBufferSize(tunnelShader.uniforms.uResolution.value)
    tunnelShader.uniforms.uTime.value = state.clock.elapsedTime
  })
}

export const tunnelShader: ShaderMaterial = new ShaderMaterial({
  side: FrontSide,
  transparent: false,
  toneMapped: false,
  uniforms: {
      uResolution: { value: new Vector2(1, 1) },
      uTime: { value: 0 },
      uOffset: { value: new Vector2(tunnelShaderOffsetX, tunnelShaderOffsetY) },
  },
  vertexShader: `
    void main() {
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: `
    precision highp float;

    uniform vec2 uResolution;
    uniform float uTime;
    uniform vec2 uOffset;

    const float TAU = 6.28318530718;
    const float STAR_TWINKLE_PHASE_WRAP = TAU * 8.0;

    float wrapPhase(float phase) {
      return mod(phase, TAU);
    }

    vec2 phaseDrift(float phaseX, float phaseY, vec2 amplitude) {
      return vec2(sin(phaseX), cos(phaseY)) * amplitude;
    }

    vec2 layeredDrift(float phaseA, float phaseB, vec2 amplitudeA, vec2 amplitudeB) {
      return phaseDrift(phaseA, phaseB, amplitudeA)
        + phaseDrift(phaseB * 0.79 + 1.3, phaseA * 0.67 - 0.8, amplitudeB);
    }

    float hash21(vec2 p) {
      p = fract(p * vec2(123.34, 456.21));
      p += dot(p, p + 45.32);
      return fract(p.x * p.y);
    }

    vec2 hash22(vec2 p) {
      float n = hash21(p);
      return fract(vec2(n, n * 34.123) * vec2(13.53, 97.17));
    }

    float valueNoise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);

      float a = hash21(i);
      float b = hash21(i + vec2(1.0, 0.0));
      float c = hash21(i + vec2(0.0, 1.0));
      float d = hash21(i + vec2(1.0, 1.0));

      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    }

    mat2 rotate2D(float angle) {
      float s = sin(angle);
      float c = cos(angle);
      return mat2(c, -s, s, c);
    }

    float fbm(vec2 p) {
      float value = 0.0;
      float amplitude = 0.5;
      for (int i = 0; i < 4; i++) {
        value += valueNoise(p) * amplitude;
        p = rotate2D(0.65) * p * 2.02 + vec2(8.7, 3.4);
        amplitude *= 0.5;
      }
      return value;
    }

    float magicSwirlField(vec2 uv, float time, vec2 warpA, vec2 warpB) {
      vec2 swirlUv = uv;
      float radius = length(swirlUv);
      float angle = atan(swirlUv.y, swirlUv.x);
      vec2 routeOffsetA = layeredDrift(time * 0.12, -time * 0.08, vec2(0.88, 0.58), vec2(0.24, 0.16));
      vec2 routeOffsetB = layeredDrift(-time * 0.09, time * 0.14, vec2(0.76, 0.84), vec2(0.22, 0.19));
      vec2 routeOffsetC = layeredDrift(time * 0.16, -time * 0.11, vec2(0.92, 0.48), vec2(0.28, 0.15));
      float routeWarpA = fbm(vec2(angle * 0.8, radius * 2.6) + routeOffsetA + warpA * 0.35);
      float routeWarpB = fbm(vec2(angle * -0.65, radius * 3.1) + routeOffsetB + warpB * 0.4);
      float routeWarpC = fbm(swirlUv * 1.9 + routeOffsetC - (warpA + warpB) * 0.25);
      float warpedAngleA = angle + (routeWarpA - 0.5) * 1.15 + sin(wrapPhase(radius * 5.8 - time * 0.6)) * 0.08;
      float warpedAngleB = angle + (routeWarpB - 0.5) * 1.35 + cos(wrapPhase(radius * 4.4 + time * 0.75)) * 0.1;
      float warpedAngleC = angle + (routeWarpC - 0.5) * 1.55;
      float warpedRadiusA = radius + (routeWarpB - 0.5) * 0.22;
      float warpedRadiusB = radius + (routeWarpC - 0.5) * 0.28;
      float warpedRadiusC = radius + (routeWarpA - 0.5) * 0.18;
      float bendA = sin(wrapPhase(warpedAngleA * 2.8 + warpedRadiusA * 8.9 - time * 1.25 + routeWarpC * 4.8)) * 0.16;
      float bendB = sin(wrapPhase(warpedAngleB * -3.9 + warpedRadiusB * 6.6 + time * 0.95 + routeWarpA * 5.2)) * 0.14;
      float bendC = sin(wrapPhase((swirlUv.x - swirlUv.y) * 4.1 - time * 0.7 + routeWarpB * 6.1)) * 0.12;
      float bandA = 1.0 - smoothstep(0.02, 0.06, abs(sin(wrapPhase(warpedAngleA * 2.2 + warpedRadiusA * 8.4 - time * 1.1 + bendA))));
      float bandB = 1.0 - smoothstep(0.022, 0.065, abs(sin(wrapPhase(warpedAngleB * -2.85 + warpedRadiusB * 7.1 + time * 0.88 + bendB))));
      float bandC = 1.0 - smoothstep(0.026, 0.07, abs(sin(wrapPhase((swirlUv.x - swirlUv.y) * 4.8 + warpedAngleC * 1.8 - time * 0.68 + bendC))));
      float gateA = smoothstep(0.48, 0.78, fbm(vec2(warpedAngleA * 0.7, warpedRadiusA * 2.0) + layeredDrift(time * 0.18 + 3.1, time * 0.11 - 1.7, vec2(0.72, 0.44), vec2(0.18, 0.12))));
      float gateB = smoothstep(0.52, 0.82, fbm(vec2(warpedAngleB * -0.6, warpedRadiusB * 2.4) + layeredDrift(-time * 0.15 + 7.8, time * 0.09 + 0.6, vec2(0.66, 0.52), vec2(0.16, 0.14))));
      float gateC = smoothstep(0.56, 0.84, fbm(vec2(warpedAngleC * 0.9, warpedRadiusC * 1.7) + layeredDrift(time * 0.12 + 12.6, -time * 0.1 + 2.4, vec2(0.62, 0.38), vec2(0.14, 0.11))));
      bandA *= gateA;
      bandB *= gateB;
      bandC *= gateC;
      float field = bandA + bandB * 0.8 + bandC * 0.55;
      float fade = smoothstep(1.25, 0.08, radius);
      return field * fade;
    }

    float starSparkle(vec2 starUv) {
      vec2 p = abs(starUv);
      float starShape = max(p.x, p.y) + min(p.x, p.y) * 2.8;
      float core = 1.0 - smoothstep(0.018, 0.11, starShape);
      float glow = 1.0 - smoothstep(0.08, 0.3, starShape);
      float verticalPeak =
        exp(-p.x * 42.0) *
        exp(-p.y * 6.5) * 0.55;
      float horizontalPeak =
        exp(-p.y * 42.0) *
        exp(-p.x * 6.5) * 0.55;
      return core + glow * 0.16 + verticalPeak + horizontalPeak;
    }

    float layeredStars(vec2 uv, float cellScale, float threshold, float sparkle, float sizeMultiplier) {
      vec2 gridUv = uv * cellScale;
      vec2 baseCell = floor(gridUv);
      float stars = 0.0;

      for (int offsetX = -1; offsetX <= 1; offsetX++) {
        for (int offsetY = -1; offsetY <= 1; offsetY++) {
          vec2 cell = baseCell + vec2(float(offsetX), float(offsetY));
          vec2 starOffset = vec2(
            hash21(cell + 4.31),
            hash21(cell + 9.17)
          ) - 0.5;
          float starMask = step(threshold, hash21(cell + 17.31));
          vec2 starCenter = (cell + 0.5 + starOffset * 0.55) / cellScale;
          vec2 starUv = (uv - starCenter) * sizeMultiplier;
          float twinkleAngularSpeed = sparkle * (0.45 + hash21(cell + 23.1) * 0.32);
          float twinklePhase = sin(
            mod(uTime * twinkleAngularSpeed, STAR_TWINKLE_PHASE_WRAP) +
            hash21(cell) * TAU
          );
          float twinkle = 0.08 + 0.92 * (0.5 + 0.5 * twinklePhase);
          stars += starSparkle(starUv) * starMask * twinkle;
        }
      }

      return stars;
    }

    void main() {
      vec2 screenUv = gl_FragCoord.xy / uResolution;
      vec2 centeredUv = screenUv - 0.5;
      centeredUv.x *= uResolution.x / max(uResolution.y, 0.0001);
      centeredUv += uOffset;

      float time = uTime * 0.055;
      vec2 macroDrift = layeredDrift(time * 0.32, -time * 0.21, vec2(0.82, 0.34), vec2(0.28, 0.14));
      vec2 flowUv = centeredUv * 2.45;
      vec2 warpOffsetA = layeredDrift(time * 1.2, -time * 0.75, vec2(0.92, 0.56), vec2(0.26, 0.18));
      vec2 warpOffsetB = layeredDrift(4.7 - time * 0.68, 1.9 + time * 1.05, vec2(0.74, 0.88), vec2(0.24, 0.17));
      vec2 warpOffsetC = layeredDrift(-time * 0.58, time * 0.88, vec2(0.86, 0.62), vec2(0.22, 0.16));
      vec2 warpOffsetD = layeredDrift(8.1 + time * 0.74, -3.6 - time * 0.52, vec2(0.8, 0.54), vec2(0.2, 0.15));
      vec2 warpA = vec2(
        fbm(centeredUv * 0.95 + warpOffsetA),
        fbm(centeredUv * 0.95 + warpOffsetB)
      ) - 0.5;
      vec2 warpB = vec2(
        fbm(centeredUv * 1.6 + warpOffsetC),
        fbm(centeredUv * 1.6 + warpOffsetD)
      ) - 0.5;
      vec2 nebulaUvA = rotate2D(wrapPhase(time * 0.42)) * (flowUv + warpA * 1.9);
      vec2 nebulaUvB = rotate2D(wrapPhase(-time * 0.31)) * (flowUv * 1.55 + warpB * 1.35);
      vec2 nebulaUvC = rotate2D(wrapPhase(time * 0.24)) * (flowUv * 3.1 + (warpA + warpB) * 0.9);
      float nebulaA = fbm(nebulaUvA + macroDrift + layeredDrift(time * 1.1, -time * 0.72, vec2(0.96, 0.62), vec2(0.22, 0.16)));
      float nebulaB = fbm(nebulaUvB - macroDrift * 0.65 + layeredDrift(time * 0.62, time * 0.45, vec2(0.72, 0.46), vec2(0.18, 0.13)));
      float nebulaC = fbm(nebulaUvC + macroDrift * 0.4 + layeredDrift(-time * 0.34, time * 0.4, vec2(0.58, 0.38), vec2(0.14, 0.11)));

      float nebula = nebulaA * 0.55 + nebulaB * 0.3 + nebulaC * 0.15;
      nebula = smoothstep(0.42, 0.9, nebula);
      float nebulaPresence = pow(nebula, 1.3);

      float dust = fbm(centeredUv * 8.0 + warpB * 0.8 - layeredDrift(time * 1.4, -time * 0.8, vec2(1.04, 0.58), vec2(0.22, 0.14)));
      dust = pow(dust, 4.2) * 0.08;

      float darkPocketField = fbm(
        centeredUv * 3.2
        - warpA * 0.75
        + warpB * 0.28
        + layeredDrift(time * 0.2, -time * 0.14, vec2(0.62, 0.36), vec2(0.18, 0.12))
      );
      float darkPocketDetail = fbm(
        centeredUv * 7.4
        + warpB * 0.55
        - layeredDrift(time * 0.34, time * 0.11, vec2(0.56, 0.28), vec2(0.14, 0.1))
      );
      float darkPockets =
        smoothstep(0.5, 0.8, darkPocketField) * (1.0 - smoothstep(0.1, 0.7, nebulaPresence));
      darkPockets +=
        smoothstep(0.62, 0.84, darkPocketDetail) *
        0.3 *
        (1.0 - smoothstep(0.18, 0.78, nebulaPresence));
      darkPockets = clamp(darkPockets, 0.0, 0.9);

      vec3 deepSpace = vec3(0.001, 0.002, 0.015);
      vec3 violet = vec3(0.14, 0.04, 0.3);
      vec3 cyan = vec3(0.04, 0.34, 0.72);
      vec3 magenta = vec3(0.72, 0.14, 0.46);

      float colorBand = smoothstep(-0.55, 0.75, centeredUv.y + centeredUv.x * 0.4 - macroDrift.x * 0.18 + nebulaB * 0.3);
      vec3 nebulaColor = mix(violet, cyan, colorBand);
      nebulaColor = mix(nebulaColor, magenta, nebulaC * nebulaPresence * 0.5);
      vec3 swirlColor = mix(nebulaColor, vec3(0.96, 0.9, 0.98), 0.22);
      float swirlField = magicSwirlField(centeredUv * 0.62 + warpA * 0.08, time, warpA, warpB);
      float swirls = swirlField;
      swirls *= smoothstep(0.24, 0.9, nebulaPresence + dust * 0.6);

      float coreGlow = exp(-length(centeredUv - vec2(-0.12, 0.04)) * 3.5) * 0.35;

      float starsLarge = layeredStars(centeredUv + layeredDrift(time * 0.7, -time * 0.3, vec2(1.18, 0.52), vec2(0.24, 0.14)), 78.0, 0.991, 3.2, 42.0);
      float starsSmall = layeredStars(centeredUv + layeredDrift(-time * 0.4, time * 0.2, vec2(0.84, 0.38), vec2(0.18, 0.12)), 168.0, 0.997, 5.4, 64.0) * 0.42;
      float starsTiny = layeredStars(centeredUv + layeredDrift(-time * 0.2, time * 0.5, vec2(0.62, 0.31), vec2(0.12, 0.08)), 276.0, 0.9992, 7.1, 88.0) * 0.08;
      float stars = starsLarge + starsSmall + starsTiny;

      vec3 background = deepSpace;
      background += nebulaColor * nebulaPresence * 1.15;
      background += nebulaColor * dust * 0.4;
      background += swirlColor * swirls * 2.05;
      background += vec3(0.52, 0.68, 0.98) * coreGlow * (0.55 + nebulaPresence * 0.35);
      background *= 1.0 - darkPockets * 0.88;

      vec3 color = background;
      color += vec3(1.0, 0.96, 0.92) * stars;

      gl_FragColor = vec4(color, 1.0);
    }`
})
